/**
 * Which palette a store paints with: `v1` (production's) unless PostHog's `ui-v2` flag is on for
 * its restaurant, assigned per `restaurant_id` like `photo-coverage-alerts`.
 *
 * The flag arrives after the app has booted, so public/index.html paints the first frame with the
 * value the last boot stored here (`bz-ui-flag`), and this module keeps that value current. When
 * the flag changes it is also applied to the open page: `v2` only merges colours no one can tell
 * apart (docs/migracion-ui/paleta-v2.md), so the switch is invisible, and a rollback takes one
 * refresh instead of two. A tab that asked for a palette with `?ui=` keeps the one it asked for.
 */
export const PALETTE_FLAG = 'ui-v2';
export const PALETTE_KEY = 'bz-ui-flag';

/** The palette the flag gives: `v2` only when it is on, so a missing or unknown flag is `v1`. */
export const paletteFor = (enabled) => (enabled === true ? 'v2' : 'v1');

/**
 * @param {{ onFeatureFlags: Function, isFeatureEnabled: Function }} posthog
 * @param {{ local?: Storage, session?: Storage, root?: HTMLElement }} [env]
 */
export function syncPaletteFlag(posthog, env = {}) {
    const root = env.root ?? document.documentElement;
    return posthog.onFeatureFlags((flags, variants, context) => {
        // Flags that failed to load say nothing about the store: keep what the last boot stored.
        if (context?.errorsLoading) return;
        const ui = paletteFor(posthog.isFeatureEnabled(PALETTE_FLAG));
        // Even reading `window.localStorage` can throw, where site data is blocked.
        try { (env.local ?? window.localStorage).setItem(PALETTE_KEY, ui); } catch { /* the next boot paints v1 */ }
        let asked = null;
        try { asked = (env.session ?? window.sessionStorage).getItem('bz-ui'); } catch { /* nothing was asked */ }
        if (!asked && root.getAttribute('data-ui') !== ui) root.setAttribute('data-ui', ui);
    });
}
